import { SCORE_BASELINE, getScoreBand, getPercentileText } from "@/lib/scoreBands";

// Curve of every company scanned, with the average and this company marked.
// Mirrors score_distribution_svg() in backend/server.py so the web view matches the PDF.
const WIDTH = 820;
const HEIGHT = 130;
const PAD_X = 12;
const TOP = 30;
const AXIS_Y = HEIGHT - 22;

const x = (v) => PAD_X + (v / 100) * (WIDTH - 2 * PAD_X);
const y = (v) => AXIS_Y - v * (AXIS_Y - TOP);

export default function ScoreDistributionChart({ score, className = "" }) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const { color } = getScoreBand(s);
  const { mean, curve } = SCORE_BASELINE;

  const points = curve.map((c, i) => `${x(i).toFixed(1)},${y(c).toFixed(1)}`);
  const fullArea = `M${x(0)},${AXIS_Y} L${points.join(" L")} L${x(100)},${AXIS_Y} Z`;
  const upto = Math.floor(s);
  const youArea = `M${x(0)},${AXIS_Y} L${points.slice(0, upto + 1).join(" L")} L${x(upto)},${AXIS_Y} Z`;
  const youY = y(curve[upto]);

  // Put each label on the outer side of its line so they never collide.
  const youRight = s >= mean;

  return (
    <div className={className} data-testid="score-distribution">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <span className="text-xs font-semibold text-[#6b7280] uppercase tracking-[0.08em]">How you compare</span>
        <span className="text-sm font-semibold text-[#374151]">{getPercentileText(s)}</span>
      </div>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full h-auto block" role="img"
        aria-label={`Score distribution: average ${mean}, this company ${s}`}>
        <path d={fullArea} fill="#e2e8f0" />
        <path d={youArea} fill={color} fillOpacity="0.35" />
        <polyline points={points.join(" ")} fill="none" stroke="#94a3b8" strokeWidth="1.5" />
        <line x1={x(0)} y1={AXIS_Y} x2={x(100)} y2={AXIS_Y} stroke="#cbd5e1" strokeWidth="1" />
        {[0, 20, 40, 60, 80, 100].map((t) => (
          <text key={t} x={x(t)} y={HEIGHT - 6} fontSize="10" fill="#94a3b8" textAnchor="middle">{t}</text>
        ))}
        <line x1={x(mean)} y1={TOP - 18} x2={x(mean)} y2={AXIS_Y} stroke="#475569" strokeWidth="1.5" strokeDasharray="4 3" />
        <text x={x(mean) + (youRight ? -6 : 6)} y={TOP - 8} fontSize="11" fill="#475569" textAnchor={youRight ? "end" : "start"}>
          Average: {mean}
        </text>
        <line x1={x(s)} y1={TOP - 18} x2={x(s)} y2={AXIS_Y} stroke={color} strokeWidth="2.5" />
        <circle cx={x(s)} cy={youY} r="5" fill={color} stroke="white" strokeWidth="2" />
        <text x={x(s) + (youRight ? 6 : -6)} y={TOP - 8} fontSize="12" fontWeight="700" fill={color} textAnchor={youRight ? "start" : "end"}>
          You: {s}
        </text>
      </svg>
    </div>
  );
}
